import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  ClickAwayListener,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tooltip
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import {
  CleanPage,
  DataTable,
  FaIcon,
  FullPageLoader,
  If,
  MDBox,
  MDButton,
  MDInput,
  MDTypography,
  ProjectDetailsModal
} from '@components/index';
import MainLayout from '@layouts/MainLayout';
import {
  useAcceptAndImportProjectMutation,
  useGetProjectByIdQuery,
  useGetProjectFieldListQuery,
  useUpdateProjectFieldDetailMutation,
  useUpdateProjectMutation
} from '@api/projects';
import { ColumnTypes } from '@constants/common';
import { Dirtcolumn, ProjectDetails } from '@DStypes/project';
import {
  setCleanPageData,
  setCurrentProjectData,
  setCurrentProjectFields,
  setProjectAnonymisation,
  setProjectFieldImportStatus,
  setProjectFieldName,
  setProjectFieldType
} from '@pages/ProjectBoard/slice';
import { RootState } from '@store/store';
import { containsWhiteSpace, convertToExcelColumnNumber } from '@utils/common';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { setUserNotifications } from '@components/Notification/slice';
import { NotificationData, MSG_EVENT, MSG_TYPE } from '@DStypes/notifications';
import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import {
  CHANGED_ANONYMISATION_STATUS,
  CLICKED_AUTO_CLEAN,
  CLICKED_KNOW_MORE,
  CLICKED_SELF_CLEAN,
  FILE_REVIEW,
  INFO_SENSITIVE,
  VISITED_REVIEW_PAGE
} from '@constants/eventNames';
import useCustomNavigate from '@hooks/useCustomNavigate';
import { PricingPlans } from '@constants/pricing';

interface SensitiveCell {
  id: string;
  fieldname: string;
  treat_as_type: string;
  to_import: boolean;
  contains_sensitive: boolean;
  field_fav: boolean;
  data_facts: string;
  clean_percentage: string;
  empty_rows: number;
  unique_values: number;
  file_field_type_counts: string;
}
export const formatColumnName = (column: string) => {
  if (column.length <= 50) return column;

  return `${column.slice(0, 50)}...`;
};

export const getColumnFacts = (
  fieldType: string,
  dataFact: any,
  missingRows: number,
  uniqueValues: number
) => {
  const textString = `Unique values: ${uniqueValues} | Missing Values: ${missingRows} | Info : First In List is ${dataFact?.min} and Last In List is ${dataFact?.max}`;
  const dateString = `Unique values: ${uniqueValues} | Missing Values: ${missingRows} | Info : Is between ${dataFact?.min} and  ${dataFact?.max}`;
  const defaultString = `Unique values: ${uniqueValues} | Missing Values: ${missingRows} | Info : Range is from ${dataFact?.min} to ${dataFact?.max}`;

  switch (fieldType) {
    case ColumnTypes.TEXT:
      return textString;
    case ColumnTypes.DATE:
    case ColumnTypes.DATETIME:
      return dateString;
    case ColumnTypes.EMPTY:
      return '';
    default:
      return defaultString;
  }
};

const ProjectReview: FC = () => {
  const { projectId } = useParams();
  const headerHeight =
    document.getElementById('header-section')?.clientHeight || 0;

  const dispatch = useDispatch();
  const navigate = useCustomNavigate();
  const { data, isLoading } = useGetProjectFieldListQuery({
    projectId
  });

  const { data: projectDetails } = useGetProjectByIdQuery({ projectId });

  const projectBoard = useSelector((state: RootState) => state?.projectBoard);

  const [updateColumnDetails] = useUpdateProjectFieldDetailMutation();

  const [isProjectDetailsModalOpen, setIsProjectDetailsModalOpen] =
    useState(false);
  const [openErrorSnackBar, setOpenErrorSnackBar] = useState(false);
  const [openAlertSnackBar, setOpenAlertSnackBar] = useState(false);
  const [showEditIcon, setShowEditIcon] = useState(false);
  const [isCleanPageModalOpen, setIsCleanPageModalOpen] = useState(false);

  const [updateProject] = useUpdateProjectMutation();
  const [acceptAndImportProject] = useAcceptAndImportProjectMutation();

  const handleCloseProjectDetailsModal = () =>
    setIsProjectDetailsModalOpen(false);

  const onUpdateProjectDetails = (projectDetails: ProjectDetails) => {
    updateProject({ id: projectId, payload: projectDetails });
    setIsProjectDetailsModalOpen(false);
    dispatch(setCurrentProjectData(projectDetails));
  };
  const handleCloseSnackbar = () => setOpenErrorSnackBar(false);

  const partialUploadRowLimit = useSelector((state: RootState) => {
    if (
      projectBoard?.currentProjectData?.plan_at_creation?.plan_code ===
      PricingPlans.NO_ACTIVE_SUBSCRIPTION
    )
      return state?.AppData?.freePlanFeatures?.ROW_LIMIT;
    else return state?.AppData?.litePlanFeatures?.ROW_LIMIT;
  });

  const partialUploadColumnLimit = useSelector((state: RootState) => {
    if (
      projectBoard?.currentProjectData?.plan_at_creation?.plan_code ===
      PricingPlans.NO_ACTIVE_SUBSCRIPTION
    )
      return state?.AppData?.freePlanFeatures?.COLUMNS_LIMIT;
    else return state?.AppData?.litePlanFeatures?.COLUMNS_LIMIT;
  });

  const currentProjectData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData
  );

  const cleanPageData = useSelector(
    (state: RootState) => state?.projectBoard?.cleanPageData
  );

  const excludedTypes = ['DATE', 'TIME'];

  useEffect(() => {
    if (projectDetails) dispatch(setCurrentProjectData(projectDetails));
  }, [projectDetails]);

  const [selectedSensitiveColumns, setSelectedSensitiveColumns] = useState<
    SensitiveCell[]
  >([]);

  const defaultDataTypes = data?.fields?.map(
    (field: any) => field.treat_as_type
  );

  useEffect(() => {
    setSelectedSensitiveColumns(
      (projectBoard.currentProjectFieldList?.fields as SensitiveCell[])?.filter(
        (row) => row.contains_sensitive && row.to_import
      )
    );
  }, [projectBoard.currentProjectFieldList?.fields]);

  useEffect(() => {
    if (currentProjectData?.id && currentProjectData?.id === projectId)
      sendAmplitudeData(VISITED_REVIEW_PAGE, {
        file_type: currentProjectData?.is_api_connected
          ? 'API'
          : currentProjectData?.project_datafiles?.[0]?.filetype
      });
  }, [currentProjectData?.id]);

  const handleAnonymisationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    sendAmplitudeData(CHANGED_ANONYMISATION_STATUS, {
      status: e.target.checked ? 'on' : 'off'
    });

    if (cleanPageData?.project_id) {
      const modifiedCleanPageData = {
        ...cleanPageData,
        dirt_columns: cleanPageData?.dirt_columns.map((column: Dirtcolumn) => {
          if (column.id === id)
            return {
              ...column,
              default_action_choice: {
                ...column.default_action_choice,
                ANONYMIZATION: e.target.checked ? 'ANONYMIZE' : 'LEAVE_AS_IS'
              }
            };
          else return column;
        })
      };

      dispatch(setCleanPageData(modifiedCleanPageData));
    }
    dispatch(
      setProjectAnonymisation({
        id: id,
        treat_as_non_sensitive: !e.target.checked
      })
    );
    updateColumnDetails({
      id: id,
      treat_as_non_sensitive: !e.target.checked
    });
  };

  const handleImportSwitchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string,
    type: string,
    cell: any
  ) => {
    dispatch(
      setProjectFieldImportStatus({ id: id, importStatus: e.target.checked })
    );
    updateColumnDetails({
      id: id,
      to_import: e.target.checked
    });

    if (cell.row.original.contains_sensitive === true && e.target.checked)
      setOpenAlertSnackBar(true);
  };

  const handleColumnTypeChange = (e: any, id: string, to_import: boolean) => {
    dispatch(setProjectFieldType({ id: id, type: e.target.value }));
    updateColumnDetails({
      treat_as_type: e.target.value,
      id: id,
      to_import: to_import
    });
  };

  const EditableCell = ({ value: initialValue, row }: any) => {
    const [value, setValue] = useState(initialValue);
    const [showText, setShowText] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleUpdateColumnName = ({ value, row }: any) => {
      const columnNames = projectBoard?.currentProjectFieldList?.fields.map(
        (field: any) => field.fieldname
      );

      const duplicateColumnNames = columnNames.filter(
        (name: string) => name === value.trim()
      );

      if (duplicateColumnNames.length && value.trim() !== initialValue) {
        setError(true);
        setErrorMessage('This column name already exists.');
      } else {
        dispatch(
          setProjectFieldName({ id: row.original.id, value: value.trim() })
        );
        setShowText(true);
        if (value.trim() !== initialValue)
          updateColumnDetails({
            id: row.original.id,
            fieldname: value.trim()
          });
      }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value.length > 50) {
        setError(true);
        setErrorMessage('Character limit 50 exceeded');
      } else {
        setError(false);
        setErrorMessage('');
      }
      setValue(e.target.value);
    };

    const handleEnterAndAwayClick = () => {
      if (value.trim().length && !error) handleUpdateColumnName({ value, row });
      else setError(true);
    };

    const typographyRef = useRef<HTMLDivElement>();

    const showTooltip =
      typographyRef?.current &&
      typographyRef?.current?.getBoundingClientRect().height > 19
        ? true
        : false;

    return showText ? (
      <>
        {row.original.contains_sensitive === true ? (
          <Grid
            container
            display='flex'
            flexDirection='row'
            alignItems='center'
            flexWrap='nowrap'
          >
            <Grid item>
              <Tooltip
                title={
                  !row.original.treat_as_non_sensitive ? (
                    <>
                      DataSquirrel has identified this column as containing
                      sensitive data (e.g., Name, email ID, etc.). The data in
                      this column will be automatically anonymised/changed
                      before saving to protect privacy (e.g., Sam Peter becomes
                      T-01b0d3a7bdd7ea33a).
                      <div style={{ fontWeight: 'bold' }}>
                        To disable anonymisation, turn this button off.
                      </div>
                      <div
                        role='presentation'
                        onClick={() => {
                          sendAmplitudeData(INFO_SENSITIVE);
                          sendAmplitudeData(CLICKED_KNOW_MORE, {
                            'Feature Name': 'Sensitive Data'
                          });
                        }}
                      >
                        <Link
                          href='https://datasquirrel.notion.site/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290'
                          target='_blank'
                          sx={{ borderBottom: '1px solid #fff' }}
                        >
                          Click to know more
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      To anonymise, turn this button on
                      <div
                        role='presentation'
                        onClick={() => {
                          sendAmplitudeData(INFO_SENSITIVE);
                          sendAmplitudeData(CLICKED_KNOW_MORE, {
                            'Feature Name': 'Sensitive Data'
                          });
                        }}
                      >
                        <Link
                          href='https://datasquirrel.notion.site/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290'
                          target='_blank'
                          sx={{ borderBottom: '1px solid #fff' }}
                        >
                          Click to know more
                        </Link>
                      </div>
                    </>
                  )
                }
              >
                <Switch
                  defaultChecked={!row.original.treat_as_non_sensitive}
                  onChange={(e) =>
                    handleAnonymisationChange(e, row.original.id)
                  }
                  disabled={
                    row.values.treat_as_type === 'EMPTY' ||
                    !row.values.to_import
                  }
                  color='primary'
                  sx={({ palette }) => ({
                    '& .MuiSwitch-switchBase': {
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: `${palette.primary.main} !important`,
                        borderColor: `${palette.primary.main} !important`
                      },
                      '&:not(.Mui-checked) + .MuiSwitch-track': {
                        backgroundColor: `${palette.grey[600]}`,
                        borderColor: `${palette.grey[600]}`
                      }
                    }
                  })}
                />
              </Tooltip>
            </Grid>
            <Grid item onClick={() => sendAmplitudeData(INFO_SENSITIVE)}>
              <Tooltip
                title='This column may contain sensitive data (Ex: Name, e-mail ID, Payment details).'
                placement='bottom'
              >
                <a
                  href='https://www.notion.so/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290'
                  target='_blank'
                  rel='noreferrer'
                >
                  <IconButton sx={{ mb: '5px' }}>
                    <FaIcon
                      icon='user-lock'
                      typographyProps={{ variant: 'xl', color: 'primary' }}
                    />
                  </IconButton>
                </a>
              </Tooltip>
            </Grid>
            <Grid
              item
              onDoubleClick={() => {
                setShowText(false);
              }}
            >
              <MDBox
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                <Tooltip
                  title={showTooltip ? row.original.fieldname : ''}
                  placement='top'
                >
                  <MDTypography
                    ref={typographyRef}
                    color='info'
                    variant='sm'
                    sx={{ textOverflow: 'ellipsis', wordBreak: 'break-all' }}
                  >
                    {row.original.fieldname}
                  </MDTypography>
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        ) : (
          <MDBox
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            <Tooltip
              title={showTooltip ? row.original.fieldname : ''}
              placement='top'
            >
              <MDTypography
                ref={typographyRef}
                color='info'
                variant='sm'
                onDoubleClick={() => {
                  setShowText(false);
                }}
                sx={{
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-all'
                }}
              >
                {row.original.fieldname}{' '}
              </MDTypography>
            </Tooltip>
          </MDBox>
        )}
      </>
    ) : (
      <>
        <ClickAwayListener onClickAway={handleEnterAndAwayClick}>
          <MDInput
            sx={{ backgroundImage: 'none' }}
            value={value}
            error={error}
            onChange={onInputChange}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === 'Escape')
                handleEnterAndAwayClick();
            }}
          />
        </ClickAwayListener>
        {error && errorMessage && (
          <FormHelperText error>{errorMessage}</FormHelperText>
        )}
      </>
    );
  };

  const getChangeInDataTypeValue = () => {
    const selectedDataTypes = projectBoard.currentProjectFieldList.fields.map(
      (field: any) => field.treat_as_type
    );

    const changeInDataTypeFlag = defaultDataTypes.filter(
      (dataType: string, index: number) => selectedDataTypes[index] === dataType
    );

    if (changeInDataTypeFlag.length === selectedDataTypes.length) return 'No';
    else return 'Yes';
  };

  const getChangeInAnonymisationValue = () => {
    const columns = selectedSensitiveColumns.map((column) => column.fieldname);

    if (columns.length) return 'Yes';
    else return 'No';
  };

  const handleAcceptAndImportClick = () => {
    const importFlag = projectBoard.currentProjectFieldList.fields.filter(
      (field: any) => field.to_import === false
    );

    if (
      importFlag.length === projectBoard.currentProjectFieldList.fields.length
    ) {
      setOpenErrorSnackBar(true);
    } else {
      const columns = selectedSensitiveColumns.map(
        (column) => column.fieldname
      );

      if (columns.length) {
        const payload: NotificationData = {
          created_at: dayjs(new Date())
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          is_read: false,
          is_silent: true,
          is_user_message: true,
          message_event: MSG_EVENT.DATA_ANONYMIZED,
          message_text: `${columns?.join(', ')} ${
            columns.length > 1 ? 'have' : 'has'
          } been automatically anonymised. Read more about this in <click here>`,
          updated_at: dayjs(new Date())
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
          message_type: MSG_TYPE.INFO,
          message_data: JSON.stringify({
            project: { id: projectId || '', name: projectDetails.project_name }
          }),
          is_important: false,
          recipient_obj: 0,
          linkKeyword: 'What is Data Anonymisation, and Why?',
          replaceKeyword: '<click here>',
          isAnchorElement: true,
          feature_name: 'Sensitive Data'
        };

        dispatch(setUserNotifications(payload));
      }

      sendAmplitudeData(CLICKED_AUTO_CLEAN);

      sendAmplitudeData(FILE_REVIEW, {
        'File Type': projectBoard.currentProjectFieldList?.file_type,
        'File ID': projectDetails.id,
        'Name of the file/project': projectDetails.project_name,
        'Change in Data Type': getChangeInDataTypeValue(),
        'Change in Data Anonymisation': getChangeInAnonymisationValue()
      });
      sendGAData(FILE_REVIEW, {
        'File Type': projectBoard.currentProjectFieldList?.file_type,
        'File ID': projectDetails.id,
        'Name of the file/project': projectDetails.project_name,
        'Change in Data Type': getChangeInDataTypeValue(),
        'Change in Data Anonymisation': getChangeInAnonymisationValue()
      });
      acceptAndImportProject({ id: projectId, is_auto_clean: true });
      navigate(`/project/${projectId}/edit`);
    }
  };

  const pageTitle = useMemo(() => {
    if (
      projectBoard?.currentProjectData?.project_remark &&
      projectBoard.currentProjectFieldList?.file.file_rows &&
      projectBoard.currentProjectFieldList?.file.file_columns &&
      partialUploadColumnLimit &&
      partialUploadRowLimit
    ) {
      const rows =
        projectBoard.currentProjectFieldList?.file.file_rows >
        partialUploadRowLimit
          ? partialUploadRowLimit
          : projectBoard.currentProjectFieldList?.file.file_rows;
      const columns =
        projectBoard.currentProjectFieldList?.file.file_columns >
        partialUploadColumnLimit
          ? partialUploadColumnLimit
          : projectBoard.currentProjectFieldList?.file.file_columns;

      return `${rows} Rows / ${columns} Columns `;
    } else if (
      projectBoard.currentProjectFieldList?.file.file_rows &&
      projectBoard.currentProjectFieldList?.file.file_columns
    ) {
      return `${projectBoard.currentProjectFieldList?.file.file_rows} Rows / ${projectBoard.currentProjectFieldList?.file.file_columns} Columns `;
    } else {
      return '';
    }
  }, [
    projectBoard?.currentProjectData?.project_remark,
    projectBoard.currentProjectFieldList?.file.file_columns,
    projectBoard.currentProjectFieldList?.file.file_rows,
    partialUploadColumnLimit,
    partialUploadRowLimit
  ]);

  useEffect(() => {
    if (data) dispatch(setCurrentProjectFields(data));
  }, [data]);

  useEffect(() => {
    if (openAlertSnackBar) setTimeout(() => setOpenAlertSnackBar(false), 6000);
  }, [openAlertSnackBar]);

  const ReviewTableColumns = useMemo(
    () => [
      {
        Header: () => (
          <Tooltip
            title='Turn the button On or Off for a column. Only columns selected as ON will be imported and saved.'
            placement='top'
          >
            <span>Import</span>
          </Tooltip>
        ),
        accessor: 'to_import',
        width: '5%',
        Cell: ({ cell }: any) => (
          <div>
            <Tooltip
              title={
                cell.row.values.treat_as_type === 'EMPTY'
                  ? 'Empty fields will not be imported.'
                  : ''
              }
              placement='top'
            >
              <div>
                <Switch
                  defaultChecked={cell.row.original.to_import}
                  onChange={(e) =>
                    handleImportSwitchChange(
                      e,
                      cell.row.original.id,
                      cell.row.values.treat_as_type,
                      cell
                    )
                  }
                  disabled={cell.row.values.treat_as_type === 'EMPTY'}
                  color='default'
                />
              </div>
            </Tooltip>
          </div>
        )
      },
      {
        Header: () => (
          <Tooltip
            title='Column Number as in your uploaded file.'
            placement='top'
          >
            <span>#</span>
          </Tooltip>
        ),
        accessor: 'index',
        width: '5%',
        Cell: ({ cell }: any) => (
          <>{convertToExcelColumnNumber(Number(cell.row.id) + 1)}</>
        )
      },
      {
        Header: () => (
          <Tooltip
            title='Column Name as in your uploaded file. You can change it if you like.'
            placement='top'
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 2,
                width: 'min-content'
              }}
            >
              <span style={{ width: 'max-content' }}>Column Name</span>
              <FaIcon
                icon='circle-question'
                style={{
                  verticalAlign: 'middle'
                }}
                typographyProps={{
                  lineHeight: 0.625,
                  color: 'info'
                }}
              />
            </div>
          </Tooltip>
        ),
        accessor: 'fieldname',
        width: '20%',
        Cell: EditableCell
      },
      {
        Header: () => (
          <Tooltip
            title='Type of column as determined by DataSquirrel. You can change it in case it is incorrectly determined - it helps us improve our recommendations for you'
            placement='right'
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: 2,
                width: 'min-content'
              }}
            >
              <span>Type</span>
              <FaIcon
                icon='circle-question'
                style={{
                  verticalAlign: 'middle'
                }}
                typographyProps={{
                  color: 'info',
                  lineHeight: 0.625
                }}
              />
            </div>
          </Tooltip>
        ),
        accessor: 'treat_as_type',
        width: '10%',
        Cell: ({ cell }: any) => (
          <>
            <Select
              sx={{
                padding: '8px',
                minWidth: '125px'
              }}
              defaultValue={
                excludedTypes.includes(cell.row.values.treat_as_type)
                  ? 'DATETIME'
                  : cell.row.values.treat_as_type
              }
              disabled={cell.row.values.treat_as_type === ColumnTypes.EMPTY}
              onChange={(e) =>
                handleColumnTypeChange(
                  e,
                  cell.row.original.id,
                  cell.row.values.to_import
                )
              }
              IconComponent={ExpandMore}
            >
              {cell.row.values.treat_as_type === ColumnTypes.EMPTY
                ? projectBoard.currentProjectFieldList.type_choices.map(
                    (type: any) => (
                      <MenuItem key={type[0]} value={type[0]}>
                        <MDTypography variant='sm' color='info'>
                          {type[1] === 'Id' ? 'ID' : type[1]}
                        </MDTypography>
                      </MenuItem>
                    )
                  )
                : projectBoard.currentProjectFieldList.type_choices
                    .filter((type: any) => type[0] !== ColumnTypes.EMPTY)
                    .map((type: any) => (
                      <MenuItem key={type[0]} value={type[0]}>
                        <MDTypography variant='sm' color='sm'>
                          {type[1] === 'Id' ? 'ID' : type[1]}
                        </MDTypography>
                      </MenuItem>
                    ))}
            </Select>
          </>
        )
      },
      {
        accessor: 'hoverItem',
        width: '60%',
        Cell: ({ cell }: any) => {
          const typographyRef = useRef<HTMLDivElement>();

          const showTooltip =
            typographyRef?.current &&
            typographyRef?.current?.getBoundingClientRect().height > 19
              ? true
              : false;

          return (
            <Tooltip
              title={
                showTooltip
                  ? getColumnFacts(
                      cell.row.original.treat_as_type,
                      JSON.parse(cell.row.original.data_facts),
                      cell.row.original.empty_rows,
                      cell.row.original.unique_values
                    )
                  : ''
              }
              placement='top'
            >
              <MDBox
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                <MDTypography
                  ref={typographyRef}
                  color='info'
                  variant='sm'
                  sx={{
                    textOverflow: 'ellipsis'
                  }}
                >
                  {getColumnFacts(
                    cell.row.original.treat_as_type,
                    JSON.parse(cell.row.original.data_facts),
                    cell.row.original.empty_rows,
                    cell.row.original.unique_values
                  )}
                </MDTypography>
              </MDBox>
            </Tooltip>
          );
        }
      }
    ],
    [projectBoard]
  );

  if (!data && isLoading) return <FullPageLoader />;
  if (
    data &&
    projectDetails?.project_status &&
    projectDetails?.project_status !== 'INGESTED'
  )
    navigate(`/project/${projectDetails?.id}/edit`);

  return (
    <>
      {openErrorSnackBar && (
        <Snackbar
          open={openErrorSnackBar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
        >
          <Grid container sx={{ flex: 1, justifyContent: 'center' }}>
            <Alert
              severity='error'
              sx={{ marginTop: '10px' }}
              onClose={handleCloseSnackbar}
            >
              Please select at-least one column to import.
            </Alert>
          </Grid>
        </Snackbar>
      )}
      <MainLayout
        pageTitle={pageTitle}
        sectionTitle='Review'
        sectionTooltip='This step helps you see an overview of the columns in the uploaded data.'
        titleTooltip='Total number of rows and columns in the uploaded data.'
        showUploadLimitMessage
      >
        <If condition={!!projectBoard.currentProjectData}>
          <Grid
            display='flex'
            container
            sx={{ my: '1rem' }}
            flexDirection='column'
            id='header-section'
          >
            <Grid container display='flex' alignItems='center' mb={0.5}>
              <Grid
                container
                xs={7}
                display='flex'
                flexDirection={'row'}
                flexWrap='nowrap'
                onMouseOver={() => setShowEditIcon(true)}
                onMouseLeave={() => setShowEditIcon(false)}
              >
                <Grid container>
                  <Tooltip
                    title={
                      projectBoard.currentProjectData?.project_name &&
                      projectBoard.currentProjectData?.project_name?.length <=
                        50
                        ? ''
                        : projectBoard.currentProjectData?.project_name || ''
                    }
                    placement='top'
                  >
                    <MDTypography
                      variant='h3'
                      color='info'
                      textGradient
                      onClick={() => setIsProjectDetailsModalOpen(true)}
                      sx={{
                        cursor: 'pointer',
                        whiteSpace: 'pre-wrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: containsWhiteSpace(
                          projectBoard.currentProjectData?.project_name || ''
                        )
                          ? 'normal'
                          : 'break-all'
                      }}
                    >
                      {projectBoard.currentProjectData?.project_name || ''}
                      <FaIcon
                        icon='pencil'
                        typographyProps={{ variant: 'lg', color: 'dark' }}
                        onClick={() => setIsProjectDetailsModalOpen(true)}
                        style={{
                          opacity: showEditIcon ? 1 : 0,
                          marginLeft: '5px'
                        }}
                      />
                    </MDTypography>
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid
                container
                item
                xs={5}
                display='flex'
                justifyContent='flex-end'
                flexWrap='nowrap'
              >
                <Tooltip
                  title={
                    <div>
                      Select this option to view and easily customize your file
                      cleaning preferences using the dropdown menu. For example,
                      you can choose not to anonymise sensitive data, choose
                      formatting of your choice, and more.
                      <div
                        role='presentation'
                        onClick={() => {
                          sendAmplitudeData(CLICKED_KNOW_MORE, {
                            'Feature Name': 'Clean Data'
                          });
                        }}
                      >
                        <Link
                          href='https://datasquirrel.notion.site/What-and-how-to-Clean-data-e070801e111b4e9c9d3ae25e53e1708f'
                          target='_blank'
                          sx={{ borderBottom: '1px solid #fff' }}
                        >
                          Click to know more
                        </Link>
                      </div>
                    </div>
                  }
                >
                  <MDButton
                    size='small'
                    color='light'
                    onClick={() => {
                      setIsCleanPageModalOpen(true);
                      sendAmplitudeData(CLICKED_SELF_CLEAN);
                    }}
                    sx={{ mr: 1, p: 0.8 }}
                  >
                    <FaIcon
                      icon='broom-wide'
                      typographyProps={{ color: 'info', variant: 'xl' }}
                    />
                    <MDTypography
                      textTransform='uppercase'
                      variant='sm'
                      color='info'
                      fontWeight='bold'
                      pl={1}
                    >
                      Choose Clean Options
                    </MDTypography>
                  </MDButton>
                </Tooltip>
                <Tooltip
                  title={
                    <>
                      Select this option to let DataSquirrel automatically clean
                      data, including automatic anonymisation of columns
                      identified as sensitive data.
                      <br></br>
                      <div
                        role='presentation'
                        onClick={() => {
                          sendAmplitudeData(CLICKED_KNOW_MORE, {
                            'Feature Name': 'Clean Data'
                          });
                        }}
                      >
                        <Link
                          href='https://datasquirrel.notion.site/What-and-how-to-Clean-data-e070801e111b4e9c9d3ae25e53e1708f'
                          target='_blank'
                          sx={{ borderBottom: '1px solid #fff' }}
                        >
                          Click to know more
                        </Link>
                      </div>
                    </>
                  }
                >
                  <MDButton
                    size='small'
                    color='primary'
                    variant='gradient'
                    onClick={handleAcceptAndImportClick}
                    sx={{ p: 0.8 }}
                  >
                    <MDTypography
                      textTransform='uppercase'
                      variant='sm'
                      color='white'
                      fontWeight='bold'
                    >
                      Auto Clean & Import
                    </MDTypography>
                  </MDButton>
                </Tooltip>
              </Grid>
              {/* </MDBox> */}
            </Grid>
            <Tooltip
              title={projectBoard?.currentProjectData?.project_short_desc || ''}
              placement='bottom-start'
            >
              <MDTypography
                width='fit-content'
                variant='h5'
                color='info'
                onClick={() => setIsProjectDetailsModalOpen(true)}
                sx={{
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  opacity: projectBoard.currentProjectData?.project_short_desc
                    ? 1
                    : 0.5,
                  cursor: 'pointer',
                  wordBreak: 'normal'
                }}
              >
                {projectBoard?.currentProjectData?.project_short_desc ||
                  'Add a description to the project (it will appear in downloaded project report)'}
              </MDTypography>
            </Tooltip>
            {projectBoard?.currentProjectData?.project_long_desc &&
              !projectBoard?.currentProjectData?.project_long_desc.startsWith(
                'Junk Data:'
              ) && (
                <Tooltip
                  title={projectBoard?.currentProjectData?.project_long_desc}
                  placement='bottom-start'
                >
                  <MDTypography
                    width='fit-content'
                    variant='h5'
                    color='info'
                    sx={{
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'normal'
                    }}
                  >
                    {projectBoard?.currentProjectData?.project_long_desc}
                  </MDTypography>
                </Tooltip>
              )}
          </Grid>
        </If>
        <If condition={!projectBoard.currentProjectData}>
          <Grid container height='80px'></Grid>
        </If>

        <Grid
          container
          sx={{
            height: `calc(100vh - ${headerHeight + 170}px)`
          }}
        >
          <DataTable
            table={{
              columns: ReviewTableColumns,
              rows: projectBoard.currentProjectFieldList?.fields || []
            }}
            showTotalEntries={false}
            entriesPerPage={{
              defaultValue:
                projectBoard.currentProjectFieldList?.fields.length || 10,
              entries: []
            }}
          />
        </Grid>
        <span
          role='presentation'
          onClick={() =>
            sendAmplitudeData(CLICKED_KNOW_MORE, {
              'Feature Name': 'Terms and conditions'
            })
          }
        >
          <MDTypography
            variant='xs'
            color='info'
            fontWeight='intermediate'
            sx={{ display: 'block', marginTop: '32px' }}
          >
            The data in the file you uploaded/linked is subject to our{' '}
            <Link
              href='https://datasquirrel.notion.site/Terms-Conditions-1b41ca5daffe4d418770998ddf0e4fa7'
              target='_blank'
            >
              <MDTypography
                variant='xs'
                color='info'
                sx={{ borderBottom: '1px solid #67748e' }}
              >
                Terms & Conditions.
              </MDTypography>
            </Link>
          </MDTypography>
        </span>
      </MainLayout>
      {isProjectDetailsModalOpen && (
        <ProjectDetailsModal
          open={isProjectDetailsModalOpen}
          onClose={handleCloseProjectDetailsModal}
          onUpdateProjectDetails={onUpdateProjectDetails}
        />
      )}

      {openAlertSnackBar && (
        <Snackbar
          open={openAlertSnackBar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Alert
            severity='info'
            sx={{ width: '100%' }}
            onClose={() => setOpenAlertSnackBar(false)}
          >
            Looks like this column contains sensitive data. It will be
            automatically anonymised by us, unless you choose to NOT anonymise
            it in “Choose Clean Options” step. Read more in{' '}
            <span
              role='presentation'
              onClick={() =>
                sendAmplitudeData(CLICKED_KNOW_MORE, {
                  'Feature Name': 'Sensitive Data'
                })
              }
            >
              <Link
                href='https://datasquirrel.notion.site/What-is-Sensitive-Data-Data-Anonymisation-17f688499d2045d99ec677c037f0c290'
                sx={{ borderBottom: '1px solid #000' }}
                target='_blank'
              >
                What is Data Anonymisation, and Why?
              </Link>
            </span>
          </Alert>
        </Snackbar>
      )}
      {isCleanPageModalOpen && (
        <CleanPage
          open={isCleanPageModalOpen}
          onClose={() => setIsCleanPageModalOpen(false)}
          projectName={projectBoard?.currentProjectData?.project_name || ''}
          fileType={projectBoard.currentProjectFieldList?.file_type}
          fileId={projectDetails.id}
          dataTypeChange={getChangeInDataTypeValue()}
          anonymisationChange={getChangeInAnonymisationValue()}
        />
      )}
    </>
  );
};

export default ProjectReview;
